// client/src/App.js
import React, { useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './AuthContext';
import { DataProvider } from './DataContext';
import { auth } from './firebase';

// Composants publics
import Signup from './components/HOME/Signup';
import LoginGeneral from './components/HOME/LoginGeneral';
import MerchantSignup from './components/COMMERCANT/MerchantSignup';
import RedirectQRC from './components/DIVERS/RedirectQRC';
import RedirectionAbonnement from './components/DIVERS/RedirectionAbonnement';
import ShortLink from './components/DIVERS/ShortLink';
import AffiliatesSignup from './components/AFF/AffiliatesSignup';
import HomeAffiliates from './components/HOME/HomeAffiliates';
import HomeClient from './components/HOME/HomeClient';
import HomeCommercant from './components/HOME/HomeCommercant';
import Home from './components/HOME/Home';
import LegalPage from './components/HOME/LegalPage';
import RedirectQRCTransfertFid from './components/DIVERS/RedirectQRCTransfertFid';
import MerchantOfferSelection from './components/COMMERCANT/MerchantOfferSelection';



// Composants Client
import ClientDashboard from './components/CLIENT/ClientDashboard';
import ClientInvoices from './components/CLIENT/ClientInvoices';
import ClientSettings from './components/CLIENT/ClientSettings';
import ClientPhotoViewer from './components/CLIENT/ClientPhotoViewer';
import ClientCamera from './components/CLIENT/ClientCamera';
import ClientPromo from './components/CLIENT/ClientPromo';
import ClientDocs from './components/CLIENT/ClientDocs';
import ClientFid from './components/CLIENT/ClientFid';
import ClientFidViewer from './components/CLIENT/ClientFidViewer';
import ClientPubViewer from './components/CLIENT/ClientPubViewer';
import ClientPubComplet from './components/CLIENT/ClientPubComplet';
import ClientScanQRC from './components/CLIENT/ClientScanQRC';

// Composants Commerçants
import MerchantDashboard from './components/COMMERCANT/MerchantDashboard';
import MerchantInvoices from './components/COMMERCANT/MerchantInvoices';
import MerchantSettings from './components/COMMERCANT/MerchantSettings';
import MerchantPhotoViewer from './components/COMMERCANT/MerchantPhotoViewer';
import MerchantCamera from './components/COMMERCANT/MerchantCamera';
import MerchantGestion from './components/COMMERCANT/MerchantGestion';
import MerchantGestionStaff from './components/COMMERCANT/MerchantGestionStaff';
import MerchantGestionPromo from './components/COMMERCANT/MerchantGestionPromo';
import MerchantAnalyseTransaction from './components/COMMERCANT/MerchantAnalyseTransaction';
import MerchantDocs from './components/COMMERCANT/MerchantDocs';
import MerchantFid from './components/COMMERCANT/MerchantFid';
import MerchantFidViewer from './components/COMMERCANT/MerchantFidViewer';
import MerchantFidGestion from './components/COMMERCANT/MerchantFidGestion';
import MerchantPubViewer from './components/COMMERCANT/MerchantPubViewer';
import MerchantAbonnement from './components/COMMERCANT/MerchantAbonnement';
import MerchantScanQRC from './components/COMMERCANT/MerchantScanQRC';


// Composants commercantpending
import MerchantProcessPaiement from './components/COMMERCANT/MerchantProcessPaiement';

// Composants Admin
import AdminDashboard from './components/ADMIN/AdminDashboard';
import AdminNavbar from './components/ADMIN/AdminNavbar';
import AdminUsers from './components/ADMIN/AdminUsers';
import AdminUsersViewer from './components/ADMIN/AdminUsersViewer';
import AdminModeration from './components/ADMIN/AdminModeration';
import AdminAffiliates from './components/ADMIN/AdminAffiliates';
import AdminProfil from './components/ADMIN/AdminProfil';
import AdminCodePromo from './components/ADMIN/AdminCodePromo';
import AdminCatalogPage from './components/ADMIN/AdminCatalogPage';
import AdminRoadmap from './components/ADMIN/AdminRoadmap';
import AdminGestionRegie from './components/ADMIN/AdminGestionRegie';
import AdminMerchantGestionViewer from './components/ADMIN/AdminMerchantGestionViewer';
import AdminSupport from './components/ADMIN/AdminSupport';
import Croquettes from './components/ADMIN/Croquettes';
import CroquettesSettings from './components/ADMIN/CroquettesSettings';


// Composants Cashier
import CashierDashboard from './components/CASHIER/CashierDashboard';
import CashierSettings from './components/CASHIER/CashierSettings';
import CashierPromo from './components/CASHIER/CashierPromo';
import CashierPubViewer from './components/CASHIER/CashierPubViewer';
import CashierAnalyseTransaction from './components/CASHIER/CashierAnalyseTransaction';


// Composants Affiliates
import AffiliatesDashboard from './components/AFF/AffiliatesDashboard';
import AffiliatesProfil from './components/AFF/AffiliatesProfil';
import AffiliatesTransactions from './components/AFF/AffiliatesTransactions';
import AffiliatesDocs from './components/AFF/AffiliatesDocs';
import AffiliatesDocsViewer from './components/AFF/AffiliatesDocsViewer';
import AffiliatesPending from './components/AFF/AffiliatesPending';


// Composants mix clients / commerçants
import TransfertFid from './components/DIVERS/TransfertFid';
import SupportBubble from './components/ADMIN/SupportBubble';
import EmailVerification from './components/DIVERS/EmailVerification';
import EmailVerifiedPage from './components/DIVERS/EmailVerifiedPage';


import ScreenState from './components/shared/ScreenState';
import { LOGO_ICON } from './components/shared/BrandLogo';
import './i18n';

// Composant d'écran de chargement
function LoadingScreen() {
  return <ScreenState />;
}

// Composant de route protégée
// Redirige les clients non-CGV vers /clientdocs (la page qui affiche la modale d'acceptation)
function ClientGuard({ userCGV, children }) {
  if (userCGV !== 'accepted') return <Navigate to="/clientdocs" replace />;
  return children;
}

function ProtectedRoute({ userRole, allowedRoles, children }) {
  // Cas transitoire : on a un utilisateur (anonyme ou connecté) mais le rôle
  // n’est pas encore synchronisé depuis Firestore → on affiche le loader
  // plutôt que de renvoyer vers la landing.
  if (!userRole && auth.currentUser) {
    return <LoadingScreen />;
  }

  if (!userRole || !allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Fonction qui retourne la route par défaut en fonction du rôle utilisateur
const determineRoute = (role) => {
  switch (role) {
    case 'client':
      return '/clientdocs';                 
    case 'commercant':
      return '/merchant-dashboard';
    case 'admin':
      return '/admin-dashboard';
    case 'affiliates':                      
      return '/affiliatesdashboard';       
    case 'cashier':                         
      return '/cashierdashboard';
    case 'commercantpending':              
      return '/merchantprocesspaiement';
    case 'affiliatepending':               
      return '/affiliatespending';
    case 'AffiliateRegie':               
      return '/affiliatesdashboard';
    default:
      return '/';
  }
};

function NotFound404() {
  const navigate = useNavigate();

  // SPA servie en HTTP 200 : on signale aux moteurs de ne pas indexer cette page (évite les "Soft 404")
  React.useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  return (
    <div className="eb-screen">
      <div className="eb-screen__panel eb-fade-up">
        <img src={LOGO_ICON} alt="Ecobill" className="eb-screen__logo" />
        <span className="eb-badge">Erreur 404</span>
        <h1 className="eb-page-title">Page introuvable</h1>
        <p className="eb-screen__text">Le lien est erroné ou la page a été déplacée.</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            className="eb-btn eb-btn--secondary"
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1); // retour page précédente
              } else {
                navigate('/'); // fallback accueil si pas d’historique
              }
            }}
          >
            Page précédente
          </button>
          <button type="button" className="eb-btn eb-btn--primary" onClick={() => navigate('/')}>
            Accueil
          </button>
        </div>
      </div>
    </div>
  );
}


// ===== validation du shortId (même logique que celle que tu utilisais) =====
const SPECIALS = "!@#%&*?";
const LOWER = "abcdefghjkmnopqrstuvwxyz"; // sans i, l
const UPPER = "ABCDEFGHJKMNOPQRSTUVWXYZ"; // sans I, L
const DIGITS = "0123456789";

function isValidShortStrict(code) {
  if (!code || code.length < 6) return false; // min 6 (3 digits + 3)
  let i = 0;
  while (i < code.length && DIGITS.includes(code[i])) i++;
  const digitsCount = i;
  if (digitsCount < 1) return false;
  const s = code[digitsCount];
  if (!s || !SPECIALS.includes(s)) return false;
  const lower = code[digitsCount + 1];
  if (!lower || !LOWER.includes(lower)) return false;
  const upper = code[digitsCount + 2];
  if (!upper || !UPPER.includes(upper)) return false;
  const expectedLen = digitsCount + 3;
  return code.length === expectedLen;
}

// Met le code au format attendu: digits + spécial + [lower] + [UPPER]
// ex: "971@Pc" -> "971@pC"
function canonicalizeShortIdLoose(code) {
  const m = /^(\d+)([!@#%&*?])([A-Za-z])([A-Za-z])$/.exec(code || "");
  if (!m) return code;
  const [, digits, special, a, b] = m;
  return `${digits}${special}${a.toLowerCase()}${b.toUpperCase()}`;
}


// ===== garde de route: si le segment ne matche pas, on rend 404 ici (pas dans ShortLink) =====
function ShortLinkRoute({ children }) {
  const { pathname, search, hash } = useLocation();

  // 1) Récup segment /xxxxx
  let raw = decodeURIComponent((pathname || "").replace(/^\//, "")).trim();

  // 2) Fallback ?c=...
  if (!isValidShortStrict(raw)) {
    const params = new URLSearchParams(search || "");
    const c = params.get("c");
    if (c) raw = c;
  }

  // 3) Fallback #xxxxx
  if (!isValidShortStrict(raw)) {
    const h = decodeURIComponent((hash || "").replace(/^#/, ""));
    if (h) raw = h;
  }

  // 4) ✅ Canoniser AVANT de valider (corrige "Pc" -> "pC")
  const canonical = canonicalizeShortIdLoose(raw);

  // Si la canonisation change l'URL, on la remplace sans reload
  if (canonical && canonical !== raw) {
    window.history.replaceState(null, "", `/${canonical}`);
  }

  // 5) Valide la version canonique
  return isValidShortStrict(canonical) ? children : <NotFound404 />;
}


// Composant dédié aux routes, consommant le contexte Auth
function AppRoutes() {
  const { userRole, userCGV, loading, emailVerified, currentUser } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  // Gate vérification email (prod uniquement, hors anonymes)
  if (currentUser && !currentUser.isAnonymous && emailVerified === false) {
    return <EmailVerification />;
  }

  const hideSupportBubble = ['/clientcamera', '/merchantcamera'].some((path) =>
    location.pathname.startsWith(path),
  );

  return (
    <>
    <Routes>
      {/* Route racine : redirige vers le tableau de bord correspondant ou la page d'accueil */}
      <Route
        path="/"
        element={userRole ? <Navigate to={determineRoute(userRole)} replace /> : <Home />}
      />

      {/* Routes publiques */}
      <Route path="/signup/:email?" element={<Signup />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/logingeneral/:code?" element={userRole ? <Navigate to={determineRoute(userRole)} replace /> : <LoginGeneral />} />
      <Route path="/logingeneral" element={userRole ? <Navigate to={determineRoute(userRole)} replace /> : <LoginGeneral />} />
      <Route path="/merchantofferselection/:code?" element={<MerchantOfferSelection />} />
      <Route path="/merchantofferselection" element={<MerchantOfferSelection />} />
      <Route path="/redirectionabonnement/:code?" element={<RedirectionAbonnement />} />
      <Route path="/redirectionabonnement" element={<RedirectionAbonnement />} />
      <Route path="/merchantsignup/:code?" element={<MerchantSignup />} />
      <Route path="/merchantsignup" element={<MerchantSignup />} />
      <Route path="/redirectqrc/:uid?" element={<RedirectQRC />} />
      <Route path="/email-verified" element={<EmailVerifiedPage />} />
      <Route path="/affiliatessignup" element={<AffiliatesSignup />} />
      <Route path="/legal/:docId" element={<LegalPage />} />
      <Route path="/homeaffiliates" element={<HomeAffiliates />} />
      <Route path="/homeclient" element={<HomeClient />} />
      <Route path="/homecommercant" element={<HomeCommercant />} />
      <Route path="/:shortId" element={<ShortLinkRoute><ShortLink /></ShortLinkRoute>} />
      <Route path="/redirectqrctransfertfid/:offerId" element={<RedirectQRCTransfertFid />} />
      <Route path="/404" element={<NotFound404 />} />
      <Route path="*" element={<NotFound404 />} />

      

      
      
      {/* Routes Client */}
      <Route
        path="/client-dashboard"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientDashboard /></ClientGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientinvoices"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientInvoices /></ClientGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientsettings"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientSettings /></ClientGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientphotoviewer"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientPhotoViewer /></ClientGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientcamera"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientCamera /></ClientGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientpromo"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientPromo /></ClientGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientdocs"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientDocs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientfid"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientFid /></ClientGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientfidviewer/:merchantId"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientFidViewer /></ClientGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientpubviewer"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientPubViewer /></ClientGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientpubcomplet"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            <ClientGuard userCGV={userCGV}><ClientPubComplet /></ClientGuard>
          </ProtectedRoute>
        }
      />
    
      <Route
        path="/clientscanqrc/:uid?"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client']}>
            {/* Pas de ClientGuard : point d'entrée public (QR / shortid).
                ClientScanQRC gère lui-même sa modale CGV (anonymes + premiers visiteurs). */}
            <ClientScanQRC />
          </ProtectedRoute>
        }
      />

      {/* Routes Commerçants */}
      <Route
        path="/merchant-dashboard"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantinvoices"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantsettings"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantphotoviewer"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantPhotoViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantcamera"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantCamera />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantgestion"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantGestion />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantanalysetransaction/:customerId"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantAnalyseTransaction />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantdocs"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantDocs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantfid"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantFid />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantfidviewer/:merchantId"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantFidViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantfidgestion"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantFidGestion />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantpubviewer"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantPubViewer />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/merchantscanqrc/:uid?"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantScanQRC />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantabonnement"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantAbonnement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantgestionstaff"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantGestionStaff />
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchantgestionpromo"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercant']}>
            <MerchantGestionPromo />
          </ProtectedRoute>
        }
      />

      {/* Routes commercantpending */}
      <Route
        path="/merchantprocesspaiement"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['commercantpending']}>
            <MerchantProcessPaiement />
          </ProtectedRoute>
        }
      />  
      

      {/* Routes Mix clients / commerçants */}
<Route
        path="/transfertfid"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['client', 'commercant']}>
            <TransfertFid />
          </ProtectedRoute>
        }
      />



      {/* Routes Admin */}
      <Route
        path="/admin-dashboard"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adminnavbar"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminNavbar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adminusers"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adminusersviewer"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminUsersViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adminmoderation"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminModeration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adminaffiliates"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminAffiliates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adminprofil"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminProfil />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admincodepromo"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminCodePromo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admincatalogpage"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adminsupport"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adminroadmap"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminRoadmap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admingestionregie"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminGestionRegie />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adminmerchantgestionviewer"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <AdminMerchantGestionViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/Croquettes"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <Croquettes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/CroquettesSettings"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['admin']}>
            <CroquettesSettings />
          </ProtectedRoute>
        }
      />






      {/* Routes Cashier */}
      <Route
        path="/cashierdashboard"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['cashier']}>
            <CashierDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cashieranalysetransaction/:customerId"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['cashier']}>
            <CashierAnalyseTransaction />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cashiersettings"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['cashier']}>
            <CashierSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cashierpromo"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['cashier']}>
            <CashierPromo />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cashierpubviewer"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['cashier']}>
            <CashierPubViewer />
          </ProtectedRoute>
        }
      />

      {/* Routes Affiliates */}
      <Route
        path="/affiliatesdashboard"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['affiliates', 'AffiliateRegie']}>
            <AffiliatesDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliatesprofil"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['affiliates', 'AffiliateRegie']}>
            <AffiliatesProfil />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliatestransactions"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['affiliates', 'AffiliateRegie']}>
            <AffiliatesTransactions />
          </ProtectedRoute>
        }
      />

      <Route
        path="/affiliatesdocs"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['affiliates', 'AffiliateRegie']}>
            <AffiliatesDocs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliatesdocsviewer"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['affiliates', 'AffiliateRegie']}>
            <AffiliatesDocsViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliatespending"
        element={
          <ProtectedRoute userRole={userRole} allowedRoles={['affiliatepending']}>
            <AffiliatesPending />
          </ProtectedRoute>
        }
      />




    </Routes>
    {userRole && !hideSupportBubble && <SupportBubble />}
    </>
  );
}




function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <Router>
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;
